/**
 * Habit Form — create + edit mode.
 * Responsive: centered modal on desktop, bottom sheet on mobile.
 * Accordion-based form.
 */

import React, { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Bookmark, Calendar, CheckSquare, Settings2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useHomeStore } from '@turnifly/shared/stores'
import { useAuthStore } from '@turnifly/shared/stores'
import { useChallengeStore } from '@turnifly/shared/stores'
import { useCreateHabit, useUpdateHabit, useHabitLimit } from '@turnifly/shared/hooks'
import { useCreateChallengeHabit } from '@turnifly/shared/hooks'
import { getLocalTodayStr } from '@turnifly/shared/utils'
import { AccordionSection } from './accordion-section'
import { HabitFormBasic } from './habit-form-basic'
import { HabitFormSchedule } from './habit-form-schedule'
import { HabitFormCompletion } from './habit-form-completion'
import { HabitFormExtras } from './habit-form-extras'

type OpenSection = 'basic' | 'schedule' | 'completion' | 'extras' | null

export const HabitFormSheet: React.FC = () => {
  const { t } = useTranslation()
  const { isFabOpen, setFabOpen, editingHabit, setEditingHabit, addHabitGoalId, setAddHabitGoalId } = useHomeStore()
  const { user } = useAuthStore()
  const { addHabitChallengeId, setAddHabitChallengeId } = useChallengeStore()

  const isGoalMode = !!addHabitGoalId
  const createHabit = useCreateHabit()
  const updateHabit = useUpdateHabit()
  const createChallengeHabit = useCreateChallengeHabit()
  const { data: habitLimit } = useHabitLimit(user?.id)

  const isChallengeMode = !!addHabitChallengeId

  const isEditMode = !!editingHabit

  const [icon, setIcon] = useState('📌')
  const [name, setName] = useState('')
  const [category, setCategory] = useState('')
  const [frequency, setFrequency] = useState<'daily' | 'weekly_days' | 'custom_interval'>('daily')
  const [weeklyDays, setWeeklyDays] = useState<number[]>([])
  const [customInterval, setCustomInterval] = useState(2)
  const [timeStart, setTimeStart] = useState('')
  const [timeDuration, setTimeDuration] = useState('')
  const [valueTarget, setValueTarget] = useState('')
  const [valueUnit, setValueUnit] = useState('')
  const [proofTypes, setProofTypes] = useState<string[]>([])
  const [penalty, setPenalty] = useState('')
  const [isReminderEnabled, setIsReminderEnabled] = useState(false)
  const [openSection, setOpenSection] = useState<OpenSection>('basic')
  const [error, setError] = useState<string | null>(null)

  // Pre-populate form when editing
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!editingHabit) return
    setIcon(editingHabit.icon || '📌')
    setName(editingHabit.name || '')
    setCategory(editingHabit.category || '')
    setFrequency((editingHabit.frequency as 'daily' | 'weekly_days' | 'custom_interval') || 'daily')
    setWeeklyDays(editingHabit.weekly_days || [])
    setCustomInterval(editingHabit.custom_interval || 2)
    setTimeStart(editingHabit.time_start ? editingHabit.time_start.slice(0, 5) : '')
    setTimeDuration(editingHabit.time_duration ? String(editingHabit.time_duration) : '')
    setValueTarget(editingHabit.value_target ? String(editingHabit.value_target) : '')
    setValueUnit(editingHabit.value_unit || '')
    setProofTypes(editingHabit.proof_types || [])
    setPenalty(editingHabit.penalty_text || '')
    setIsReminderEnabled(editingHabit.reminder_enabled || false)
    setOpenSection('basic')
    setError(null)
  }, [editingHabit])
  /* eslint-enable react-hooks/set-state-in-effect */

  const resetForm = useCallback((): void => {
    setIcon('📌')
    setName('')
    setCategory('')
    setFrequency('daily')
    setWeeklyDays([])
    setCustomInterval(2)
    setTimeStart('')
    setTimeDuration('')
    setValueTarget('')
    setValueUnit('')
    setProofTypes([])
    setPenalty('')
    setIsReminderEnabled(false)
    setOpenSection('basic')
    setError(null)
  }, [])

  const handleClose = useCallback((): void => {
    setFabOpen(false)
    setEditingHabit(null)
    setAddHabitChallengeId(null)
    setAddHabitGoalId(null)
    resetForm()
  }, [setFabOpen, setEditingHabit, setAddHabitChallengeId, setAddHabitGoalId, resetForm])

  const handleSubmit = (): void => {
    if (!name.trim() || !user?.id) return
    if (!category) {
      setError(t('home.habitForm.categoryRequired'))
      return
    }

    // Check habit limit for new personal habits (not edit, not challenge)
    if (!isEditMode && !isChallengeMode && habitLimit && !habitLimit.can_create) {
      setError(t('home.habitForm.limitReached', { max: habitLimit.max_personal }))
      return
    }

    setError(null)

    const hasValue = !!valueTarget
    const hasDuration = !!timeDuration && Number(timeDuration) > 0
    const timeMode = hasDuration ? 'duration' : timeStart ? 'time_range' : 'anytime'

    if (isEditMode && editingHabit) {
      // Edit mode — only send changed fields
      const updates: Record<string, unknown> = {
        name: name.trim(),
        icon,
        category,
        frequency,
        weekly_days: frequency === 'weekly_days' ? weeklyDays : null,
        custom_interval: frequency === 'custom_interval' ? customInterval : null,
        time_mode: timeMode,
        time_start: timeStart || null,
        time_duration: hasDuration ? Number(timeDuration) : null,
        value_required: hasValue,
        value_target: hasValue ? Number(valueTarget) : null,
        value_unit: hasValue ? valueUnit || null : null,
        proof_types: proofTypes.length > 0 ? proofTypes : [],
        penalty_text: penalty || null,
        reminder_enabled: isReminderEnabled,
      }

      updateHabit.mutate(
        { habitId: editingHabit.id, updates },
        {
          onSuccess: handleClose,
          onError: (err) => setError(err instanceof Error ? err.message : t('common.error')),
        },
      )
    } else if (isChallengeMode && addHabitChallengeId) {
      // Challenge habit mode
      createChallengeHabit.mutate({
        challengeId: addHabitChallengeId,
        userId: user.id,
        name: name.trim(),
        icon,
        category,
        frequency,
        weeklyDays: frequency === 'weekly_days' ? weeklyDays : undefined,
        customInterval: frequency === 'custom_interval' ? customInterval : undefined,
        timeMode,
        timeStart: timeStart || undefined,
        timeDuration: hasDuration ? Number(timeDuration) : undefined,
        valueRequired: hasValue || undefined,
        valueTarget: hasValue ? Number(valueTarget) : undefined,
        valueUnit: hasValue ? valueUnit || undefined : undefined,
        proofTypes: proofTypes.length > 0 ? proofTypes : undefined,
      }, {
        onSuccess: handleClose,
        onError: (err) => setError(err instanceof Error ? err.message : t('common.error')),
      })
    } else {
      // Normal create mode (optionally linked to a goal)
      createHabit.mutate({
        userId: user.id,
        name: name.trim(),
        icon,
        category,
        frequency,
        weeklyDays: frequency === 'weekly_days' ? weeklyDays : undefined,
        customInterval: frequency === 'custom_interval' ? customInterval : undefined,
        startDate: getLocalTodayStr(),
        timeMode,
        timeStart: timeStart || undefined,
        timeDuration: hasDuration ? Number(timeDuration) : undefined,
        valueRequired: hasValue || undefined,
        valueTarget: hasValue ? Number(valueTarget) : undefined,
        valueUnit: hasValue ? valueUnit || undefined : undefined,
        proofTypes: proofTypes.length > 0 ? proofTypes : undefined,
        penaltyText: penalty || undefined,
        reminderEnabled: isReminderEnabled || undefined,
        goalId: addHabitGoalId ?? undefined,
      }, {
        onSuccess: handleClose,
        onError: (err) => setError(err instanceof Error ? err.message : t('common.error')),
      })
    }
  }

  const toggleSection = (s: OpenSection): void => {
    setOpenSection((prev) => (prev === s ? null : s))
  }

  const isPending = isEditMode ? updateHabit.isPending : createHabit.isPending

  return (
    <AnimatePresence>
      {isFabOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
          />

          {/* Container: bottom on mobile, centered on desktop */}
          <div className="fixed inset-0 z-[60] flex items-end lg:items-center lg:justify-center pointer-events-none">
            <motion.div
              initial={{ y: '100%', opacity: 0.5 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="pointer-events-auto w-full lg:max-w-lg bg-surface border-t lg:border border-border rounded-t-2xl lg:rounded-2xl max-h-[90vh] lg:max-h-[85vh] overflow-y-auto pb-safe"
            >
              {/* Header: X left, title center, action right */}
              <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-surface z-10">
                <button onClick={handleClose} className="p-2 -ml-2 text-text-hint hover:text-text tap-highlight-transparent">
                  <X size={20} />
                </button>
                <h2 className="text-[15px] font-display font-bold text-text absolute left-1/2 -translate-x-1/2">
                  {isEditMode ? t('home.editHabit') : t('home.createHabit')}
                </h2>
                <button
                  onClick={handleSubmit}
                  disabled={!name.trim() || !category || isPending}
                  className="text-[14px] font-semibold text-primary disabled:text-text-hint/40 tap-highlight-transparent transition-colors"
                >
                  {isPending ? '...' : isEditMode ? t('common.save') : t('common.create')}
                </button>
              </div>

              <div className="p-4 space-y-3">
                {/* Goal link indicator */}
                {isGoalMode && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-primary/[0.06] border border-primary/[0.12]">
                    <span className="text-sm">🎯</span>
                    <span className="text-xs text-primary font-medium">{t('home.habitForm.goalLink')}</span>
                  </div>
                )}

                <AccordionSection title={t('home.habitForm.basic')} icon={<Bookmark size={16} />}
                  isOpen={openSection === 'basic'} onToggle={() => toggleSection('basic')}>
                  <HabitFormBasic icon={icon} name={name} category={category}
                    onIconChange={setIcon} onNameChange={setName} onCategoryChange={setCategory} />
                </AccordionSection>

                <AccordionSection title={t('home.habitForm.schedule')} icon={<Calendar size={16} />}
                  isOpen={openSection === 'schedule'} onToggle={() => toggleSection('schedule')}>
                  <HabitFormSchedule
                    frequency={frequency} weeklyDays={weeklyDays} customInterval={customInterval}
                    timeStart={timeStart} timeDuration={timeDuration}
                    onFrequencyChange={setFrequency} onWeeklyDaysChange={setWeeklyDays}
                    onCustomIntervalChange={setCustomInterval}
                    onTimeStartChange={setTimeStart} onTimeDurationChange={setTimeDuration} />
                </AccordionSection>

                <AccordionSection title={t('home.habitForm.completion')} icon={<CheckSquare size={16} />}
                  isOpen={openSection === 'completion'} onToggle={() => toggleSection('completion')}>
                  <HabitFormCompletion
                    valueTarget={valueTarget} valueUnit={valueUnit} proofTypes={proofTypes}
                    onValueTargetChange={setValueTarget} onValueUnitChange={setValueUnit}
                    onProofTypesChange={setProofTypes} />
                </AccordionSection>

                <AccordionSection title={t('home.habitForm.extras')} icon={<Settings2 size={16} />}
                  isOpen={openSection === 'extras'} onToggle={() => toggleSection('extras')}>
                  <HabitFormExtras penalty={penalty} isReminderEnabled={isReminderEnabled}
                    onPenaltyChange={setPenalty} onReminderChange={setIsReminderEnabled} />
                </AccordionSection>

                {error && (
                  <div className="p-3 rounded-xl bg-danger/10 border border-danger/20 text-danger text-sm">{error}</div>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}
